/**
 * /{ws}/drafts — Drafts & sent (Slack; core DraftsViewModel): drafts synced across my devices, scheduled, sent. The
 * page header of the other top-level pages, then Slack's tab strip with counts ("Drafts 3", "Scheduled 1", "Sent").
 */
import { Shell, type SentRow } from "@bundle/core";
import { Divider, DraftsList, ScheduledList, SegmentedControl, SentList, Stack, Text, useAnnouncer, useNavigation, useScheduled, useSettingsContext, useT, useViewModel, useWorkspace } from "@bundle/ui";
import { useEffect, useState, useSyncExternalStore } from "react";

type Tab = "drafts" | "scheduled" | "sent";

const noSubscribe = () => () => {};

export function DraftsScreen() {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const announce = useAnnouncer();
  const settings = useSettingsContext();
  const vm = ws.vm.drafts;
  const scheduled = useScheduled();
  const [tab, setTab] = useState<Tab>("drafts");
  // Counts on the tabs: re-render when either list changes.
  useViewModel(vm);
  useSyncExternalStore(scheduled?.subscribe ?? noSubscribe, () => scheduled?.state.messages);
  // Opening the page takes the server's latest drafts and scheduled messages (for the counts); the Sent tab loads
  // when it is opened.
  useEffect(() => {
    void vm.refresh();
    void scheduled?.refresh();
  }, [vm, scheduled]);
  useEffect(() => {
    if (tab === "sent") void vm.loadSent();
  }, [vm, tab]);
  const open = (channelId: string, threadRootId: string | undefined, focus?: string) =>
    nav.open(
      threadRootId === undefined
        ? Shell.formatUrl(focus === undefined ? { type: "channel", ws: ws.slug, channelId } : { type: "channel", ws: ws.slug, channelId, focus })
        : Shell.formatUrl(focus === undefined ? { type: "thread", ws: ws.slug, channelId, rootId: threadRootId } : { type: "thread", ws: ws.slug, channelId, rootId: threadRootId, focus }),
    );
  // The shell's copy + web origin when it provides them (Settings context), else this page's.
  const copyLink = (row: SentRow) => {
    const url = `${settings?.origin ?? location.origin}${Shell.messageUrl(ws.slug, row.message.channelId, row.message.id, row.message.threadRootId)}`;
    const done = settings === null || settings === undefined ? navigator.clipboard.writeText(url) : settings.copy(url);
    void done.then(() => announce(t("message.linkCopied")), () => undefined);
  };
  const draftCount = vm.rows().length;
  const scheduledCount = scheduled?.rows().length ?? 0;
  const options: { value: Tab; label: string; count?: number }[] = [{ value: "drafts", label: t("drafts.tab.drafts"), count: draftCount || undefined }];
  if (scheduled !== undefined) options.push({ value: "scheduled", label: t("drafts.tab.scheduled"), count: scheduledCount || undefined });
  options.push({ value: "sent", label: t("drafts.tab.sent") });
  return (
    <Stack grow testID="drafts-screen">
      <Stack paddingX="8" paddingY="4">
        <Text variant="title2" weight="bold" heading={1}>
          {t("drafts.title")}
        </Text>
      </Stack>
      <Stack paddingX="8">
        <SegmentedControl options={options} value={tab} onChange={setTab} accessibilityLabel={t("drafts.title")} variant="tabs" testID="drafts-tabs" />
      </Stack>
      <Divider />
      {tab === "drafts" ? (
        <DraftsList vm={vm} onOpen={(row) => open(row.draft.channelId, row.draft.threadRootId)} testID="drafts" />
      ) : tab === "sent" ? (
        <SentList vm={vm} onOpen={(row) => open(row.message.channelId, row.message.threadRootId, row.message.id)} onCopyLink={copyLink} testID="sent" />
      ) : scheduled === undefined ? null : (
        <ScheduledList vm={scheduled} onOpen={(row) => open(row.scheduled.channelId, row.scheduled.threadRootId)} testID="drafts-scheduled" />
      )}
    </Stack>
  );
}
